import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DocsLanding from "@/components/docs/DocsLanding";
import DocsPageTracker from "@/components/docs/DocsPageTracker";
import { PageMeta } from "@/components/PageMeta";
import { canonicalUrl } from "@/lib/canonical";
import navigation from "@/docs-content/navigation.json";

// Build a slug -> title map from the navigation sidebar so every docs page
// gets a real, unique <title>. Example: "getting-started/quickstart" -> "Quick Start".
const docsTitleMap: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const section of navigation.sections) {
    for (const item of section.items) {
      if (item.href.startsWith("/docs/")) {
        const slug = item.href.replace(/^\/docs\//, "");
        map[slug] = item.title;
      }
    }
  }
  return map;
})();

// Map of all available docs pages (mirrors the Next.js page.tsx docsMap)
const docsMap: Record<string, () => Promise<{ default: React.ComponentType }>> = {
  "getting-started/quickstart": () => import("@/docs-content/getting-started/quickstart.mdx"),
  "getting-started/authentication": () => import("@/docs-content/getting-started/authentication.mdx"),
  "getting-started/sessions": () => import("@/docs-content/getting-started/sessions.mdx"),
  "getting-started/scopes": () => import("@/docs-content/getting-started/scopes.mdx"),
  "getting-started/rate-limits": () => import("@/docs-content/getting-started/rate-limits.mdx"),
  "getting-started/security": () => import("@/docs-content/getting-started/security.mdx"),
  "getting-started/error-handling": () => import("@/docs-content/getting-started/error-handling.mdx"),
  "value-apis/overview": () => import("@/docs-content/value-apis/overview.mdx"),
  "value-apis/ndc": () => import("@/docs-content/value-apis/ndc.mdx"),
  "value-apis/npi": () => import("@/docs-content/value-apis/npi.mdx"),
  "value-apis/rxnorm": () => import("@/docs-content/value-apis/rxnorm.mdx"),
  "value-apis/loinc": () => import("@/docs-content/value-apis/loinc.mdx"),
  "value-apis/icd10": () => import("@/docs-content/value-apis/icd10.mdx"),
  "value-apis/cvx": () => import("@/docs-content/value-apis/cvx.mdx"),
  "value-apis/mvx": () => import("@/docs-content/value-apis/mvx.mdx"),
  "value-apis/fda-labels": () => import("@/docs-content/value-apis/fda-labels.mdx"),
  "value-apis/snomed": () => import("@/docs-content/value-apis/snomed.mdx"),
  "value-apis/hcc": () => import("@/docs-content/value-apis/hcc.mdx"),
  "value-apis/opcs4": () => import("@/docs-content/value-apis/opcs4.mdx"),
  "value-apis/dmd": () => import("@/docs-content/value-apis/dmd.mdx"),
  "value-apis/ucum": () => import("@/docs-content/value-apis/ucum.mdx"),
  "value-apis/rxclass": () => import("@/docs-content/value-apis/rxclass.mdx"),
  "value-apis/hcpcs": () => import("@/docs-content/value-apis/hcpcs.mdx"),
  "value-apis/msdrg": () => import("@/docs-content/value-apis/msdrg.mdx"),
  "value-apis/pos": () => import("@/docs-content/value-apis/pos.mdx"),
  "value-apis/jcode": () => import("@/docs-content/value-apis/jcode.mdx"),
  "value-apis/ddi": () => import("@/docs-content/value-apis/ddi.mdx"),
  "connectivity/overview": () => import("@/docs-content/connectivity/overview.mdx"),
  "sma/overview": () => import("@/docs-content/sma/overview.mdx"),
  "sma/api-reference": () => import("@/docs-content/sma/api-reference.mdx"),
  "claims/overview": () => import("@/docs-content/claims/overview.mdx"),
  "claims/ncci": () => import("@/docs-content/claims/ncci.mdx"),
  "claims/mue": () => import("@/docs-content/claims/mue.mdx"),
  "claims/pfs": () => import("@/docs-content/claims/pfs.mdx"),
  "claims/coverage": () => import("@/docs-content/claims/coverage.mdx"),
  "search-apis/overview": () => import("@/docs-content/search-apis/overview.mdx"),
  "search-apis/ndc": () => import("@/docs-content/search-apis/ndc.mdx"),
  "search-apis/npi": () => import("@/docs-content/search-apis/npi.mdx"),
  "search-apis/rxnorm": () => import("@/docs-content/search-apis/rxnorm.mdx"),
  "search-apis/loinc": () => import("@/docs-content/search-apis/loinc.mdx"),
  "search-apis/icd10": () => import("@/docs-content/search-apis/icd10.mdx"),
  "search-apis/cvx": () => import("@/docs-content/search-apis/cvx.mdx"),
  "search-apis/mvx": () => import("@/docs-content/search-apis/mvx.mdx"),
  "search-apis/fda-labels": () => import("@/docs-content/search-apis/fda-labels.mdx"),
  "search-apis/snomed": () => import("@/docs-content/search-apis/snomed.mdx"),
  "search-apis/hcc": () => import("@/docs-content/search-apis/hcc.mdx"),
  "search-apis/opcs4": () => import("@/docs-content/search-apis/opcs4.mdx"),
  "search-apis/dmd": () => import("@/docs-content/search-apis/dmd.mdx"),
  "search-apis/ucum": () => import("@/docs-content/search-apis/ucum.mdx"),
  "search-apis/rxclass": () => import("@/docs-content/search-apis/rxclass.mdx"),
  "search-apis/hcpcs": () => import("@/docs-content/search-apis/hcpcs.mdx"),
  "search-apis/msdrg": () => import("@/docs-content/search-apis/msdrg.mdx"),
  "shl/overview": () => import("@/docs-content/shl/overview.mdx"),
  "shl/quickstart": () => import("@/docs-content/shl/quickstart.mdx"),
  "shl/cli-reference": () => import("@/docs-content/shl/cli-reference.mdx"),
  "shl/server-guide": () => import("@/docs-content/shl/server-guide.mdx"),
  "shl/storage-adapters": () => import("@/docs-content/shl/storage-adapters.mdx"),
  "shl/pshd": () => import("@/docs-content/shl/pshd.mdx"),
  "shl/security": () => import("@/docs-content/shl/security.mdx"),
  "shl/ehr-integration": () => import("@/docs-content/shl/ehr-integration.mdx"),
  "shl/troubleshooting": () => import("@/docs-content/shl/troubleshooting.mdx"),
  "response-shapes": () => import("@/docs-content/response-shapes.mdx"),
  "mcp/overview": () => import("@/docs-content/mcp/overview.mdx"),
  "mcp/claude-desktop": () => import("@/docs-content/mcp/claude-desktop.mdx"),
  "mcp/how-it-works": () => import("@/docs-content/mcp/how-it-works.mdx"),
  "mcp/api-reference": () => import("@/docs-content/mcp/api-reference.mdx"),
  "legal/terms": () => import("@/docs-content/legal/terms.mdx"),
  "legal/privacy": () => import("@/docs-content/legal/privacy.mdx"),
  "legal/acceptable-use": () => import("@/docs-content/legal/acceptable-use.mdx"),
  "legal/cookies": () => import("@/docs-content/legal/cookies.mdx"),
  "legal/data-sources": () => import("@/docs-content/legal/data-sources.mdx"),
};

export default function DocsPage() {
  const location = useLocation();
  const [Content, setContent] = useState<React.ComponentType | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);

  // Extract slug from pathname: "/docs/getting-started/quickstart" -> "getting-started/quickstart"
  const slug = location.pathname.replace(/^\/docs\/?/, "").replace(/\/$/, "") || "";

  useEffect(() => {
    // Reset state on slug change
    setContent(null);
    setNotFound(false);

    if (!slug) {
      // No slug means landing page; no content to load
      return;
    }

    const loader = docsMap[slug];
    if (!loader) {
      setNotFound(true);
      return;
    }

    setLoading(true);
    loader()
      .then((mod) => {
        setContent(() => mod.default);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  // Landing page
  if (!slug) {
    return (
      <>
        <PageMeta
          title="Documentation"
          description="Complete FHIRfly documentation: API reference, SDKs, MCP for AI agents, SMART Health Links, claims intelligence, and integration guides for every healthcare data type."
          canonical={canonicalUrl("/docs")}
        />
        <DocsLanding />
      </>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    );
  }

  // Not found
  if (notFound || !Content) {
    return (
      <div className="py-12 px-4 text-center">
        <h1 className="text-2xl font-bold text-text mb-2">Page Not Found</h1>
        <p className="text-text-quiet">
          The documentation page <code>{slug}</code> does not exist.
        </p>
      </div>
    );
  }

  const pageTitle = docsTitleMap[slug];
  return (
    <>
      <PageMeta
        title={pageTitle ? `${pageTitle} — Docs` : "Documentation"}
        description={
          pageTitle
            ? `${pageTitle} — FHIRfly developer documentation.`
            : "FHIRfly developer documentation."
        }
        canonical={canonicalUrl(`/docs/${slug}`)}
      />
      <DocsPageTracker slug={slug} />
      <Content />
    </>
  );
}
